import { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import {
  defaultEntityGroupsExportFieldNames,
  entityGroupsExportFields,
} from "~pages/EntityGroups/EntityGroupListPage/constants/exportFields.ts";
import { useExtractEntityGroupsFromScope } from "~pages/EntityGroups/EntityGroupListPage/hooks/useExtractEntityGroupsFromScope.ts";
import { useFilteredEntityGroups } from "~pages/EntityGroups/EntityGroupListPage/hooks/useFilteredEntityGroups.ts";
import { useGetEntityGroupTableColumns } from "~pages/EntityGroups/EntityGroupListPage/hooks/useGetEntityGroupTableColumns.tsx";
import { useGetEntityGroupTableFilters } from "~pages/EntityGroups/EntityGroupListPage/hooks/useGetEntityGroupTableFilters.tsx";
import { useEntityGroupService } from "~pages/EntityGroups/hooks/useEntityGroupService.ts";

const EntityGroupListPage = () => {
  const { sport, organizationId, customerId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { uiConfiguration } = useSportConfiguration();
  const location = useLocation();
  const dataTableUiConfiguration = uiConfiguration.pages.entityGroups.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.entityGroups.listPage.importConfig;
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const paginationConfig = useHandlePagination(20);

  const columns = useGetEntityGroupTableColumns();
  const filterConfig = useGetEntityGroupTableFilters();

  const { entityGroups, isLoading, refetch } = useExtractEntityGroupsFromScope();

  const filteredEntityGroups = useFilteredEntityGroups({
    entityGroups,
    currentFilter: filterConfig.currentFilter,
    searchText,
  });
  const { deleteEntityGroup, isDeletePending, importEntityGroups, exportEntityGroups, isExportPending } = useEntityGroupService();

  const getNavigateUrl = (entityGroupId: string) => `/${customerId}/admin/${sport}/${organizationId}/entity-groups/manage/${entityGroupId}`;

  return (
    <DataTable
      title={formatMessage({ id: "entityGroupListPage.title" })}
      columns={columns}
      handleRefresh={async () => {
        await refetch();
      }}
      filtersConfig={filterConfig}
      isDataLoading={isLoading}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => deleteEntityGroup(row.entityGroupId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.entityGroupId)),
          },
        },
      }}
      handleAdd={() => navigate(`/${customerId}/admin/${sport}/${organizationId}/entity-groups/manage`)}
      addButtonIntlKey={"entityGroup"}
      rowKey={"entityGroupId"}
      rowNameKey={"nameFullLocal"}
      dataSource={filteredEntityGroups}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.entityGroups`}
      columnSortConfig={{
        sorterLocalStorageKey: `tables.settings.sort.entityGroups`,
        defaultSorter: {
          columnKey: "nameFullLocal",
          order: "ascend",
        },
      }}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      exportConfig={{
        handleExport: exportEntityGroups,
        loading: isExportPending,
        defaultFields: defaultEntityGroupsExportFieldNames,
        groupedFields: entityGroupsExportFields,
        localStorageKey: "export.entityGroups",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: importEntityGroups,
      }}
      permissionNames={[PermissionNames.ENTITY_GROUP]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, total: filteredEntityGroups.length }}
    />
  );
};

export default EntityGroupListPage;
